import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';

import { DownloadButton, GradButton, MenuButton } from '@components/buttons';
import CheckBox from '@components/check-box/CheckBox';
import SalesChannelMultiSelect from '@components/inputs/SalesChannelMultiSelect';
import { GLOBAL_MK } from '@sm/Flags';
import { SELECTOR_VIEW_HEIGHT } from '@sm/layout';
import { AMAZON_SALES_CHANNEL_ID } from '@sm/SalesChannels';
import { setMarketplaceAction, setSelectedSalesChannelsAction } from 'seller/store/actions';
import {
  bulkUpdateHcogsAction,
  pullProductsAction,
  setMissingInfoFlagAction,
  setProductStatusAction,
  updateProductsAction
} from 'seller/store/saga/products';
import UploadPhotoDialog from '../../pages/profile/UploadPhotoDialog';
import MarketplaceSelector from '../selector-view/MarketplaceSelector';
import getProductsFromCSV, { exportCSV, getCsvFileName } from './csvHelpers';
import HistoricalCogsBulkUpdate from './HistoricalCogsBulkUpdate';
import BulkUploadIcon from './ic_cloud-upload-alt-solid.svg';

const csvColumns = {
  name: 'Product',
  asin: 'ASIN',
  sku: 'SKU',
  is_amazon_fulfilled: 'Fulfilled by Amazon',
  unit_cost: 'Product CoGS',
  unit_shipping_cost: 'Inbound Shipping',
  fbm_shipping_cost: 'FBM Shipping',
  fbm_return_cost: 'Cost per FBM Return',
  currency: 'Currency',
  marketplace: 'Marketplace',
  inventory: 'Current Inventory',
  profit: 'Profit'
};

const ACTIVE = 'active';
const ALL = 'all';

export const PRODUCT_STATUS_MAP = {
  active: 'Active',
  inactive: 'Inactive',
  deleted: 'Deleted',
  archived: 'Archived',
  draft: 'Draft',
  all: 'All'
};

const AMAZON_STATUSES = [ACTIVE, 'inactive', 'deleted', ALL];
const SHOPIFY_STATUSES = [ACTIVE, 'draft', 'archived', ALL];

const LeftView = props => {
  const {
    marketplaces,
    marketplaceID,
    onMarketplaceChanged,
    showOnlyWithMissingInfo,
    dispatch,
    setStatus,
    status,
    selectedSalesChannels,
    setSelectedChannels,
    isAmazonSalesChannel,
    profile
  } = props;
  const activeStatuses = isAmazonSalesChannel ? AMAZON_STATUSES : SHOPIFY_STATUSES;
  const filteredStatusesMap = useMemo(
    () =>
      Object.keys(PRODUCT_STATUS_MAP).reduce((acc, key) => {
        if (activeStatuses.includes(key)) acc[key] = PRODUCT_STATUS_MAP[key];
        return acc;
      }, {}),
    [activeStatuses]
  );
  useEffect(() => {
    if (!Object.keys(filteredStatusesMap).includes(status)) setStatus(activeStatuses[0]);
  }, [status, setStatus, filteredStatusesMap, activeStatuses]);
  return (
    <div className="flex flex-1 justify-start items-center pl-2 md:pl-0">
      <SalesChannelMultiSelect
        selectedSalesChannels={selectedSalesChannels}
        setSelectedChannels={setSelectedChannels}
        salesChannelFilter={({ id }) => profile.sales_channels.includes(id)}
        className="mr-2"
        singleSelectMode
      />
      <MarketplaceSelector
        marketplaces={marketplaces}
        marketplaceID={marketplaceID}
        onMarketplaceChanged={onMarketplaceChanged}
        className={classNames({ hidden: !isAmazonSalesChannel })}
      />

      <MenuButton
        name={PRODUCT_STATUS_MAP[status]}
        data={Object.values(filteredStatusesMap)}
        bullet={null}
        className={classNames('ml-4')}
        onSelected={item => {
          const itemKey = Object.keys(PRODUCT_STATUS_MAP).find(
            key => PRODUCT_STATUS_MAP[key] === item
          );
          // console.log(itemKey);
          setStatus(itemKey);
          // setFirstMetric(item);
          // onPrimaryMetricChanged(getMetricByTitle(item));
        }}
      />

      <CheckBox
        height={16}
        width={16}
        label="Show only with missing info."
        classNameContainer={classNames('ml-2', { hidden: !isAmazonSalesChannel })}
        value={showOnlyWithMissingInfo}
        onCheckHandler={check => dispatch(setMissingInfoFlagAction(check))}
      />
    </div>
  );
};

LeftView.propTypes = {
  onMarketplaceChanged: PropTypes.func.isRequired,
  marketplaces: PropTypes.instanceOf(Array).isRequired,
  marketplaceID: PropTypes.number.isRequired,
  showOnlyWithMissingInfo: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  selectedSalesChannels: PropTypes.instanceOf(Array).isRequired,
  setSelectedChannels: PropTypes.func.isRequired,
  isAmazonSalesChannel: PropTypes.bool.isRequired,
  profile: PropTypes.instanceOf(Object).isRequired
};

class ProductSelectorView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showBulkUploadDialog: false
    };
  }

  render() {
    const {
      setMarketplace,
      profile,
      marketplaceID,
      showOnlyWithMissingInfo,
      productsList,
      status,
      dispatch,
      selectedSalesChannels,
      setSelectedChannels
    } = this.props;
    const isAmazonSalesChannel = selectedSalesChannels.includes(AMAZON_SALES_CHANNEL_ID);
    const { showBulkUploadDialog } = this.state;
    const visibleMarketplaces = profile.marketplaces.filter(
      i => i !== GLOBAL_MK || profile.historical_cogs_bulk_upload
    );
    const csvFileName = getCsvFileName({ dashboardName: 'Products', marketplaceId: marketplaceID });
    const setStatus = newStatus => dispatch(setProductStatusAction(newStatus));
    const onDownloadTemplate = () => exportCSV(productsList, csvFileName, csvColumns);

    const onFileUploaded = (result, date = null) => {
      const products = getProductsFromCSV(result);
      const action = date ? bulkUpdateHcogsAction : updateProductsAction;
      dispatch(
        action({
          products,
          onRequestFinish: success => {
            if (success) dispatch(pullProductsAction());
          },
          isBulkUpload: true,
          date
        })
      );
    };
    return (
      <div
        style={{ height: SELECTOR_VIEW_HEIGHT }}
        className="flex flex-row flex-shrink-0 relative 10 box-shadow-light overflow-visible bg-white lg:px-6 xs:px-2 justify-between items-center 10 z-10"
      >
        <LeftView
          onChange={this.onSearchInputChange}
          openSearchDialog={() => this.setState(prevState => ({ ...prevState }))}
          onSearchFocus={this.onSearchFocus}
          marketplaces={visibleMarketplaces}
          marketplaceID={marketplaceID}
          onMarketplaceChanged={setMarketplace}
          showOnlyWithMissingInfo={showOnlyWithMissingInfo}
          dispatch={dispatch}
          status={status}
          setStatus={setStatus}
          selectedSalesChannels={selectedSalesChannels}
          setSelectedChannels={setSelectedChannels}
          isAmazonSalesChannel={isAmazonSalesChannel}
          profile={profile}
        />
        <DownloadButton
          className={classNames('mr-4 hidden', { 'md:flex': isAmazonSalesChannel })}
          paddingY="py-0"
          fileName={csvFileName}
          columns={csvColumns}
          data={productsList}
          onClick={(data, fileName, columns) => exportCSV(data, fileName, columns)}
        >
          Your Template
        </DownloadButton>
        {profile.historical_cogs_bulk_upload && isAmazonSalesChannel && (
          <HistoricalCogsBulkUpdate
            className="h-8 bg-grad-8 mr-4 hidden md:flex"
            lifetime={profile.start}
            onDownloadTemplate={onDownloadTemplate}
            onFileUploaded={onFileUploaded}
          />
        )}
        <GradButton
          className={classNames('h-8 bg-grad-8 hidden', {
            'md:flex': isAmazonSalesChannel
          })}
          onClick={() =>
            this.setState(prevState => {
              return { ...prevState, showBulkUploadDialog: true };
            })
          }
        >
          <div className="flex flex-row items-center">
            <img src={BulkUploadIcon} alt="arrow-left" className="h-4 w-4 mr-2" />
            Bulk Upload
          </div>
        </GradButton>

        <UploadPhotoDialog
          onUploadHandler={onFileUploaded}
          open={showBulkUploadDialog}
          onClose={() =>
            this.setState(prevState => {
              return { ...prevState, showBulkUploadDialog: false };
            })
          }
          title="Bulk Upload Product Costs"
          placeholder="Click to select, or drag and drop your CSV file here"
          mask=".csv"
          loadFileFunc={(file, reader) => reader.readAsText(file)}
          loadedView={() => <div className="text-gray-dark">Attached</div>}
        />
      </div>
    );
  }
}

ProductSelectorView.propTypes = {
  setMarketplace: PropTypes.func.isRequired,
  profile: PropTypes.instanceOf(Object).isRequired,
  marketplaceID: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  showOnlyWithMissingInfo: PropTypes.bool.isRequired,
  productsList: PropTypes.instanceOf(Array).isRequired,
  status: PropTypes.string.isRequired,
  selectedSalesChannels: PropTypes.instanceOf(Array).isRequired,
  setSelectedChannels: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    productsList: state.products.productsList,
    profile: state.user.profile,
    marketplaceID: state.selector.marketplaceID,
    showOnlyWithMissingInfo: state.products.showOnlyWithMissingInfo,
    status: state.selector.productStatus,
    selectedSalesChannels: state.selector.selectedSalesChannels
  };
}

const mapDispatchToProps = dispatch => ({
  dispatch,
  setMarketplace: marketplace => dispatch(setMarketplaceAction(marketplace)),
  setSelectedChannels: value => dispatch(setSelectedSalesChannelsAction(value))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelectorView);
